import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { GET_USER_PROFILES_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { UserProfile, UserProfilesResponse } from '../../integrations/allkort/graphql/interface/Customer';

const PROFILES_PER_PAGE = 5;
const MAX_PROFILES = 500;

/**
 * Hook for fetching ALL user profiles under currentCustomer with automatic pagination.
 * Fetches 5 at a time and follows hasNextPage until all profiles are loaded.
 * Uses network-only since user profiles change after mutations.
 *
 * Usage:
 * const { userProfiles, loading, error, isComplete } = useGetAllUserProfiles();
 */
export const useGetAllUserProfiles = () => {
	const [allProfiles, setAllProfiles] = useState<UserProfile[]>([]);
	const [currentCursor, setCurrentCursor] = useState<string | null>(null);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	const variables = useMemo(() => ({
		first: PROFILES_PER_PAGE,
		after: currentCursor,
		last: null,
		before: null,
	}), [currentCursor]);

	const { data, loading, error } = useQuery<UserProfilesResponse>(
		GET_USER_PROFILES_QUERY,
		{
			variables,
			fetchPolicy: 'network-only',
			skip: isComplete,
		}
	);

	useEffect(() => {
		if (loading) return; // skip while new query is in-flight — data may be stale from previous cursor
		if (!data?.currentCustomer?.userProfiles) return;

		const { nodes, pageInfo } = data.currentCustomer.userProfiles;
		if (nodes && nodes.length > 0) {
			setAllProfiles(prev => {
				if (!currentCursor) {
					return nodes;
				}
				const existingIds = new Set(prev.map(p => p.id));
				const newProfiles = nodes.filter(p => !existingIds.has(p.id));
				return [...prev, ...newProfiles];
			});

			const totalProfiles = currentCursor ? allProfiles.length + nodes.length : nodes.length;

			if (pageInfo.hasNextPage && totalProfiles < MAX_PROFILES) {
				setCurrentCursor(pageInfo.endCursor);
				setIsFetchingMore(true);
			} else {
				setIsFetchingMore(false);
				setIsComplete(true);
			}
		} else {
			setIsFetchingMore(false);
			setIsComplete(true);
		}
	}, [data, loading, currentCursor]);

	return {
		userProfiles: allProfiles,
		loading: loading || isFetchingMore,
		error,
		isComplete,
		refetch: () => {
			setAllProfiles([]);
			setCurrentCursor(null);
			setIsFetchingMore(false);
			setIsComplete(false);
		},
	};
};
